import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import VsureCheckModal from '../components/VsureCheckModal';
import { downloadAuditPack } from '../utils/visaAuditPack';
import {
  Plane, RefreshCw, AlertTriangle, CheckCircle, Clock, HelpCircle, Search,
  GraduationCap, ShieldCheck, Link2, X, FileText, MessageSquare, CalendarDays, Download,
} from 'lucide-react';

// Immigration board — the office view of every international contractor's visa and,
// crucially, their derived WORK RIGHTS (unlimited vs 48h/fortnight vs no work vs
// needs review). Work rights are computed server-side by _shared/work-rights.js from
// the captured visa details + the latest recorded VEVO check (Record VEVO — manual
// today; the vSure API will write the same record).
// Admin-only. Talks to driver-dms-sb (listImmigration).

const API = '/.netlify/functions/driver-dms-sb';
async function call(payload) {
  const res = await fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
  return res.json();
}

const RIGHT = {
  unlimited:       { label: 'Unlimited',      color: 'var(--hp-green)', bg: 'rgba(46,125,50,0.12)',  Icon: CheckCircle },
  '48h_fortnight': { label: '48h / fortnight', color: '#b8860b',        bg: 'rgba(201,168,76,0.16)', Icon: Clock },
  no_work:         { label: 'No work',         color: 'var(--hp-red)',  bg: 'rgba(214,69,69,0.12)',  Icon: AlertTriangle },
  unknown:         { label: 'Needs review',    color: 'var(--hp-dim)',  bg: 'var(--hp-surface)',     Icon: HelpCircle },
};

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
}

export default function ImmigrationPage() {
  const { addNotification } = useApp();
  const navigate = useNavigate();
  const [holders, setHolders] = useState(null);
  const [filter, setFilter] = useState('');

  // Period — drives the per-person "in this period" line and the audit pack.
  const [range, setRange] = useState(() => presetRange('fy'));
  const [periodKey, setPeriodKey] = useState(0);   // bumped by Refresh to reload the period
  const [period, setPeriod] = useState(null);       // { byLogin, former, from, to } | null
  const [periodBusy, setPeriodBusy] = useState(false);
  const [packMsg, setPackMsg] = useState('');
  useEffect(() => {
    if (!range.from || !range.to || range.from > range.to) return undefined;
    let live = true;
    setPeriodBusy(true);
    call({ action: 'visaAudit', from: range.from, to: range.to, include: 'summary' }).then((d) => {
      if (!live) return;
      if (d && d.success) {
        const byLogin = {}; d.audit.people.forEach((p) => { byLogin[p.login] = p; });
        setPeriod({ byLogin, people: d.audit.people, from: range.from, to: range.to });
      } else { setPeriod(null); addNotification?.(d?.error || 'Could not load the period', 'error'); }
    }).catch((e) => { if (live) addNotification?.(e.message, 'error'); })
      .finally(() => { if (live) setPeriodBusy(false); });
    return () => { live = false; };
  }, [range.from, range.to, periodKey, addNotification]);

  const load = useCallback(async () => {
    try {
      const d = await call({ action: 'listImmigration' });
      if (d && d.success) setHolders(d.holders || []);
      else { setHolders([]); addNotification?.(d?.error || 'Could not load immigration data', 'error'); }
    } catch (e) { setHolders([]); addNotification?.(e.message, 'error'); }
  }, [addNotification]);
  useEffect(() => { load(); }, [load]);

  // Refresh reloads BOTH the board and the period figures, and shows that it did.
  const [refreshing, setRefreshing] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState('');
  const refresh = useCallback(async () => {
    setRefreshing(true);
    setPeriodKey((k) => k + 1);
    try { await load(); } finally {
      setRefreshing(false);
      setRefreshedAt(new Date().toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', second: '2-digit' }));
    }
  }, [load]);

  const [vevoFor, setVevoFor] = useState(null);   // the holder whose check is being recorded
  const [showTest, setShowTest] = useState(false);
  const [vsureFor, setVsureFor] = useState(null);  // the holder being checked through vSure
  const [showNudge, setShowNudge] = useState(false);
  const awaitingConsent = (holders || []).filter((h) => !(h.vevoConsent && h.vevoConsent.given)).length;

  const openVevo = useCallback(async (login) => {
    const w = window.open('', '_blank');   // synchronously, so the popup blocker lets it through
    try {
      const d = await call({ action: 'getVevoFile', driverLogin: login });
      if (!d || !d.success) throw new Error(d?.error || 'Could not open the VEVO result');
      const bytes = Uint8Array.from(atob(d.base64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: d.contentType || 'application/pdf' }));
      if (w) w.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { if (w) w.close(); addNotification?.(e.message, 'error'); }
  }, [addNotification]);

  const [verifying, setVerifying] = useState('');
  const verifyLink = useCallback(async (login, verified) => {
    setVerifying(login);
    try {
      const d = await call({ action: 'verifyPartnerLink', driverLogin: login, verified });
      if (d && d.success) { addNotification?.(verified ? 'Partner link verified.' : 'Verification removed.', 'success'); await load(); }
      else addNotification?.(d?.error || 'Could not update the link', 'error');
    } catch (e) { addNotification?.(e.message, 'error'); }
    finally { setVerifying(''); }
  }, [addNotification, load]);

  const rows = useMemo(() => {
    const list = holders || [];
    if (!filter) return list;
    const f = filter.toLowerCase();
    return list.filter((h) =>
      h.login.toLowerCase().includes(f) || (h.name || '').toLowerCase().includes(f)
      || (h.category || '').toLowerCase().includes(f) || (h.subclass || '').includes(f));
  }, [holders, filter]);

  const summary = useMemo(() => {
    const l = holders || [];
    return {
      total: l.length,
      unlimited: l.filter((h) => h.workRight === 'unlimited').length,
      capped: l.filter((h) => h.workRight === '48h_fortnight').length,
      review: l.filter((h) => h.needsReview).length,
    };
  }, [holders]);

  const openInCompliance = (login) => navigate('/compliance', { state: { openDriver: login } });

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1180 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <Plane size={26} color="var(--hp-gold)" />
        <h1 style={{ margin: 0, fontFamily: 'var(--font-display)', fontSize: '1.7rem', color: 'var(--hp-text)' }}>Immigration</h1>
        <button onClick={refresh} disabled={refreshing} style={{ ...ghostBtn, opacity: refreshing ? 0.7 : 1 }} title="Reload everyone's visa records and the period figures">
          <RefreshCw size={15} className={refreshing ? 'spin' : ''} /> {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
        {refreshedAt && !refreshing && <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>Updated {refreshedAt}</span>}
        {awaitingConsent > 0 && (
          <button onClick={() => setShowNudge(true)} style={ghostBtn} title="Text everyone who hasn't consented yet">
            <MessageSquare size={15} /> Text consent request ({awaitingConsent})
          </button>
        )}
        <button onClick={() => setShowTest((v) => !v)} style={ghostBtn} title="Run a vSure sandbox check on test data">
          <ShieldCheck size={15} /> Test vSure
        </button>
      </div>
      {showTest && <VsureTestPanel onClose={() => setShowTest(false)} />}
      <p style={{ color: 'var(--hp-dim)', fontSize: '0.86rem', margin: '0 0 20px', maxWidth: 760 }}>
        Work rights for every visa-holding contractor, derived from their visa details. A coursework student is capped at 48h/fortnight;
        a Masters by research or Doctorate is unlimited. <strong>Record VEVO</strong> saves a check you ran (VEVO for Organisations or vSure) with its result PDF —
        what Home Affairs says then outranks the captured details.
      </p>

      {/* Summary tiles */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 22 }}>
        <Tile label="Visa holders" value={summary.total} color="var(--hp-text)" icon={<Plane size={16} />} />
        <Tile label="Unlimited" value={summary.unlimited} color="var(--hp-green)" icon={<CheckCircle size={16} />} />
        <Tile label="Capped (48h)" value={summary.capped} color="#b8860b" icon={<Clock size={16} />} />
        <Tile label="Needs review" value={summary.review} color={summary.review ? 'var(--hp-red)' : 'var(--hp-dim)'} icon={<HelpCircle size={16} />} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10, padding: '10px 12px', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)' }}>
        <CalendarDays size={15} color="var(--hp-gold)" />
        <span style={{ fontSize: '0.78rem', fontWeight: 800, color: 'var(--hp-text)' }}>Period</span>
        <select value={range.preset} onChange={(e) => setRange(e.target.value === 'custom' ? { ...range, preset: 'custom' } : presetRange(e.target.value))}
          style={{ ...inp, width: 'auto' }}>
          {PRESETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <input type="date" value={range.from} max={range.to} onChange={(e) => setRange({ ...range, preset: 'custom', from: e.target.value })} style={{ ...inp, width: 'auto' }} />
        <span style={{ color: 'var(--hp-dim)', fontSize: '0.8rem' }}>to</span>
        <input type="date" value={range.to} min={range.from} onChange={(e) => setRange({ ...range, preset: 'custom', to: e.target.value })} style={{ ...inp, width: 'auto' }} />
        {periodBusy && <RefreshCw size={13} className="spin" color="var(--hp-dim)" />}
        {period && (() => {
          const over = period.people.filter((p) => p.summary.fortnightsOver > 0).length;
          const former = period.people.filter((p) => !(holders || []).some((h) => h.login === p.login)).length;
          return (
            <span style={{ fontSize: '0.74rem', color: over ? 'var(--hp-red)' : 'var(--hp-dim)', fontWeight: over ? 800 : 500 }}>
              {over ? `${over} ${over === 1 ? 'person' : 'people'} over 48h in at least one fortnight` : 'No one over the 48h cap'}
              {former ? <span style={{ color: 'var(--hp-dim)', fontWeight: 500 }}> · {former} former visa {former === 1 ? 'holder' : 'holders'} in this period (in the audit pack)</span> : null}
            </span>
          );
        })()}
        <button disabled={!!packMsg && !/^(Done|Failed)/.test(packMsg)} style={{ ...ghostBtn, marginLeft: 'auto' }}
          title={filter ? 'Audit pack for the people matching the filter' : 'Audit pack for everyone who held a visa in the period, including former workers'}
          onClick={async () => {
            try {
              await downloadAuditPack({
                from: range.from, to: range.to,
                logins: filter ? rows.map((h) => h.login) : null,
                call, onProgress: setPackMsg,
              });
            } catch (e) { setPackMsg('Failed: ' + e.message); }
          }}>
          <Download size={14} /> {filter ? `Audit pack (${rows.length})` : 'Download audit pack'}
        </button>
        {packMsg && <span style={{ flexBasis: '100%', fontSize: '0.74rem', color: /^Failed/.test(packMsg) ? 'var(--hp-red)' : 'var(--hp-dim)' }}>{packMsg}</span>}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '5px 9px', background: 'var(--hp-surface)' }}>
          <Search size={13} color="var(--hp-dim)" />
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by name, category, subclass…"
            style={{ border: 'none', background: 'none', color: 'var(--hp-text)', fontSize: '0.82rem', outline: 'none', width: 220 }} />
        </div>
      </div>

      {holders === null ? (
        <div style={{ color: 'var(--hp-dim)' }}><RefreshCw size={15} className="spin" /> Loading…</div>
      ) : !rows.length ? (
        <div style={{ color: 'var(--hp-dim)', fontSize: '0.85rem', padding: 20, border: '1px dashed var(--hp-border)', borderRadius: 'var(--radius)' }}>
          No visa holders yet. A contractor appears here once their Visa Grant Notice or visa details are recorded (Compliance → driver → Visa details).
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {rows.map((h) => {
            const r = RIGHT[h.workRight] || RIGHT.unknown;
            return (
              <div key={h.login} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 14px', border: '1px solid var(--hp-border)', borderLeft: `3px solid ${r.color}`, borderRadius: 'var(--radius)', background: 'var(--hp-surface)', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 160, flex: '1 1 160px' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--hp-text)' }}>
                    {h.name}
                    {Array.isArray(h.roles) && h.roles.includes('sorter') && (
                      <span style={{ marginLeft: 6, fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', borderRadius: 999, padding: '1px 6px' }}>
                        {h.roles.includes('driver') ? 'Driver · Sorter' : 'Sorter'}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>
                    {[h.category || 'Visa', h.subclass && `subclass ${h.subclass}${h.visaName ? ` (${h.visaName})` : ''}`, h.bridging && 'bridging', h.secondary && 'partner / dependent',
                      h.conditions && h.conditions.length > 0 && `cond. ${h.conditions.join(' ')}`,
                      h.courseEndDate && `course ${h.courseEndDate < new Date().toISOString().slice(0, 10) ? 'completed' : 'ends'} ${fmtDate(h.courseEndDate)}`].filter(Boolean).join(' · ')}
                  </div>
                  {period && period.byLogin[h.login] && (() => {
                    const ps = period.byLogin[h.login].summary;
                    const bits = [];
                    if (ps.fortnightsCapped) bits.push(ps.fortnightsOver ? `${ps.fortnightsOver} of ${ps.fortnightsCapped} fortnights over 48h` : `${ps.fortnightsCapped} capped ${ps.fortnightsCapped === 1 ? 'fortnight' : 'fortnights'}, none over`);
                    if (ps.peakCounted != null) bits.push(`peak ${ps.peakCounted.toFixed(1)}h`);
                    if (ps.vevoChecksInPeriod) bits.push(`${ps.vevoChecksInPeriod} VEVO ${ps.vevoChecksInPeriod === 1 ? 'check' : 'checks'}`);
                    if (!bits.length) return null;
                    return (
                      <div style={{ fontSize: '0.7rem', marginTop: 2, fontWeight: ps.fortnightsOver ? 800 : 600, color: ps.fortnightsOver ? 'var(--hp-red)' : 'var(--hp-dim)' }}
                        title={`${fmtDate(period.from)} – ${fmtDate(period.to)}${ps.detailsUnversioned ? '\nIncludes dates before visa-detail history began (22 Sep 2026) — work rights for those dates use the earliest details on record.' : ''}`}>
                        In period: {bits.join(' · ')}
                      </div>
                    );
                  })()}
                  {h.linkedName && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3, flexWrap: 'wrap' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.7rem', fontWeight: 700, color: 'var(--hp-gold)' }}
                        title={h.linkRole === 'dependent-of'
                          ? `Work rights follow ${h.linkedName} (primary holder)`
                          : `${h.linkedName} is a partner / dependent on this visa`}>
                        <Link2 size={11} />
                        {h.linkRole === 'dependent-of' ? `Dependent of ${h.linkedName}` : `Primary for ${h.linkedName}`}
                      </span>
                      {h.linkVerified
                        ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.64rem', fontWeight: 800, color: 'var(--hp-green)', background: 'rgba(46,125,50,0.10)', borderRadius: 999, padding: '1px 7px' }} title="Office-verified: the primary's enrolment covers this dependent's CoE/LoE"><ShieldCheck size={10} /> Link verified</span>
                        : <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.64rem', fontWeight: 800, color: '#b8860b', background: 'rgba(201,168,76,0.14)', borderRadius: 999, padding: '1px 7px' }} title="Self-declared link — not yet confirmed. Doc-sharing stays off until verified.">Link pending</span>}
                      {/* The verify control lives on the DEPENDENT's row (that's where the link is stored). */}
                      {h.linkRole === 'dependent-of' && (
                        h.linkVerified
                          ? <button disabled={verifying === h.login} onClick={() => verifyLink(h.login, false)} style={{ ...ghostBtn, padding: '2px 8px', fontSize: '0.64rem' }}>Remove</button>
                          : <button disabled={verifying === h.login} onClick={() => verifyLink(h.login, true)} style={{ ...ghostBtn, padding: '2px 8px', fontSize: '0.64rem', borderColor: 'var(--hp-green)', color: 'var(--hp-green)' }}>{verifying === h.login ? '…' : 'Verify link'}</button>
                      )}
                    </div>
                  )}
                </div>

                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 11px', borderRadius: 999, fontSize: '0.74rem', fontWeight: 800, color: r.color, background: r.bg }} title={h.workBasis || ''}>
                  <r.Icon size={12} /> {r.label}
                </span>

                {/* Fortnight tally vs the 48h cap — monitoring only. Worst of the two live
                    Monday windows; amber from 40h, red past the cap. */}
                {h.workRight === '48h_fortnight' && h.fortnight && (() => {
                  const f = h.fortnight;
                  const t = f.maxTotal;   // in-session hours only — break days don't count
                  const tip = [
                    'Hours this fortnight (both Monday-aligned windows) — in session / in a course break:',
                    ...f.windows.map((w) => `${fmtDate(w.start)} – ${fmtDate(w.end)}: ${w.counted.toFixed(1)}h counted`
                      + (w.breakHours ? ` + ${w.breakHours.toFixed(1)}h in a course break (not counted)` : '')
                      + ` (sort ${w.sorter.toFixed(1)} + drive ${w.driver.toFixed(1)})`),
                    f.termsRecorded ? '' : 'No study breaks recorded — every day counts. Add their term dates in Visa details.',
                  ].filter(Boolean).join('\n');
                  if (f.onBreak) {
                    return (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 800, color: 'var(--hp-green)', background: 'rgba(46,125,50,0.10)' }}
                        title={tip}>
                        <CheckCircle size={11} /> Course break to {fmtDate(f.currentBreak.end)} · unlimited{t > 0 ? ` (${t.toFixed(1)}h in session)` : ''}
                      </span>
                    );
                  }
                  const c = t > 48 ? 'var(--hp-red)' : t >= 40 ? '#b8860b' : 'var(--hp-dim)';
                  const bg = t > 48 ? 'rgba(198,40,40,0.10)' : t >= 40 ? 'rgba(201,168,76,0.14)' : 'transparent';
                  return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 800, color: c, background: bg, border: bg === 'transparent' ? '1px solid var(--hp-border)' : '1px solid transparent' }}
                      title={tip}>
                      <Clock size={11} /> {t.toFixed(1)}h / 48h{f.breakHours > 0 ? ` +${f.breakHours.toFixed(1)}h break` : ''}
                    </span>
                  );
                })()}

                <span style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', minWidth: 120 }}>
                  {!h.expiry && h.vevo && h.vevo.noExpiry
                    ? (/^0[1-5]\d$/.test(h.vevo.subclass || '') ? 'No expiry (bridging — until a decision)' : 'No expiry')
                    : `Expires ${fmtDate(h.expiry)}`}
                </span>

                {h.vevo && h.vevo.hasFile ? (
                  <button onClick={() => openVevo(h.login)} title={vevoTip(h.vevo, h.vevoChecks)}
                    style={{ ...linkBtn, color: 'var(--hp-green)', minWidth: 110 }}>
                    <ShieldCheck size={12} /> VEVO {fmtDate(h.vevoCheckedAt)}
                  </button>
                ) : (
                  <span style={{ fontSize: '0.72rem', color: h.vevoStatus ? 'var(--hp-green)' : 'var(--hp-dim)', minWidth: 110, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <ShieldCheck size={12} /> {h.vevoStatus ? `VEVO ${fmtDate(h.vevoCheckedAt)}` : 'VEVO not checked'}
                  </span>
                )}


                {h.outstanding && h.outstanding.length > 0 && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.72rem', fontWeight: 700, color: '#b8860b' }} title="Outstanding visa documents">
                    <GraduationCap size={12} /> {h.outstanding.join(', ')}
                  </span>
                )}

                {h.vevoLastAttempt && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.7rem', fontWeight: 700, color: 'var(--hp-red)' }}
                    title={`${h.vevoLastAttempt.text}\n${h.vevoLastAttempt.by === 'VEVO-RECHECK' ? 'Monthly re-check' : h.vevoLastAttempt.by}, ${fmtDate(h.vevoLastAttempt.at.slice(0, 10))}`}>
                    <AlertTriangle size={11} /> Last VEVO check: {h.vevoLastAttempt.code.replace(/^VEVO_/, '').replace(/_/g, ' ').toLowerCase()}
                  </span>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  {h.vevoConsent && h.vevoConsent.given ? (
                    <button onClick={() => setVsureFor(h)} style={{ ...ghostBtn, padding: '4px 10px', fontSize: '0.72rem', borderColor: 'var(--hp-green)', color: 'var(--hp-green)' }}
                      title={`Consent given in the portal ${fmtDate((h.vevoConsent.at || '').slice(0, 10))}`}>
                      {h.vevoPending ? 'vSure check pending…' : 'Check with vSure'}
                    </button>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', fontWeight: 800, color: '#b8860b', background: 'rgba(201,168,76,0.18)', border: '1px solid rgba(184,134,11,0.45)', borderRadius: 999, padding: '3px 10px' }}
                      title="They need to tap “I consent” at the top of My Docs (drivers) or Documents (sorters) before a vSure check can run">
                      <Clock size={11} /> Awaiting consent
                      {h.vevoConsentNudgedAt && <span style={{ fontWeight: 600, opacity: 0.8 }}>· texted {fmtDate(h.vevoConsentNudgedAt.slice(0, 10))}</span>}
                    </span>
                  )}
                  <button onClick={() => setVevoFor(h)} style={{ ...ghostBtn, padding: '4px 10px', fontSize: '0.72rem' }} title="Record a check you ran yourself, with its PDF">
                    {h.vevo ? 'Record re-check' : 'Record VEVO'}
                  </button>
                  <button onClick={() => openInCompliance(h.login)} style={ghostBtn}>Manage →</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showNudge && (
        <ConsentSmsModal onClose={() => setShowNudge(false)}
          onSent={async (r) => {
            setShowNudge(false);
            addNotification?.(`Consent request texted to ${r.sent} ${r.sent === 1 ? 'person' : 'people'}${r.failed.length ? ` — ${r.failed.length} failed` : ''}.`, r.failed.length ? 'error' : 'success');
            await load();
          }}
          onError={(m) => addNotification?.(m, 'error')} />
      )}
      {vsureFor && (
        <VsureCheckModal name={vsureFor.name} onClose={() => setVsureFor(null)}
          load={() => call({ action: 'vsurePrepare', driverLogin: vsureFor.login })}
          run={(p) => call({ action: 'vsureCheckPerson', driverLogin: vsureFor.login, ...p })}
          viewScan={(prep) => prep.passportId && call({ action: 'getDocFile', id: prep.passportId })}
          consentLabel="Consent given in the portal"
          onSaved={async () => { setVsureFor(null); addNotification?.('VEVO check saved from vSure.', 'success'); await load(); }}
          onError={(m) => addNotification?.(m, 'error')} />
      )}
      {vevoFor && (
        <VevoCheckModal holder={vevoFor} onClose={() => setVevoFor(null)}
          onSaved={async () => { setVevoFor(null); addNotification?.('VEVO check recorded.', 'success'); await load(); }}
          onError={(m) => addNotification?.(m, 'error')} />
      )}
    </div>
  );
}

// vSure connection test — runs one of vSure's SANDBOX test passports through the real
// login → check → PDF path. Never a real person, never writes a record.
const TEST_CASES = [
  ['student_8105', 'Student 500 with 8105 (should be capped)'],
  ['graduate_485', 'Temporary Graduate 485 (should be unlimited)'],
  ['bridging_8101', 'Bridging E with 8101 (should be no work)'],
  ['not_found', 'Person not found (should error cleanly)'],
];
function VsureTestPanel({ onClose }) {
  const [tc, setTc] = useState(TEST_CASES[0][0]);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    setBusy(true); setRes(null);
    try {
      let d = await call({ action: 'vsureSelfTest', testCase: tc });
      // A check still pending after the server's budget: ask again by id.
      for (let i = 0; d && d.success && d.pending && i < 6; i += 1) {
        await new Promise((r) => setTimeout(r, 4000));
        d = await call({ action: 'vsureSelfTest', checkId: d.checkId });
      }
      setRes(d);
    } catch (e) { setRes({ success: false, error: e.message }); }
    finally { setBusy(false); }
  };
  const r = res && res.success ? res : null;
  const right = r && r.workRight ? (RIGHT[r.workRight] || RIGHT.unknown) : null;
  return (
    <div style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)', padding: '14px 16px', margin: '0 0 18px', maxWidth: 760 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <strong style={{ fontSize: '0.88rem', color: 'var(--hp-text)' }}>vSure connection test (sandbox)</strong>
        <button onClick={onClose} style={{ ...linkBtn, marginLeft: 'auto' }} aria-label="Close"><X size={15} /></button>
      </div>
      <p style={{ margin: '0 0 10px', fontSize: '0.76rem', color: 'var(--hp-dim)' }}>
        Sends one of vSure's published test passports through the real login, check and PDF download. Sandbox only, so no real person and no charge, and nothing is saved.
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={tc} onChange={(e) => setTc(e.target.value)} disabled={busy} style={{ ...inp, width: 'auto', maxWidth: '100%' }}>
          {TEST_CASES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <button onClick={run} disabled={busy} style={ghostBtn}>{busy ? <><RefreshCw size={13} className="spin" /> Checking…</> : 'Run test'}</button>
      </div>
      {res && !res.success && (
        <div style={{ marginTop: 10, fontSize: '0.8rem', color: 'var(--hp-red)' }}><AlertTriangle size={13} /> {res.error}</div>
      )}
      {r && r.pending && <div style={{ marginTop: 10, fontSize: '0.8rem', color: '#b8860b' }}>Still pending at vSure after the wait — check {r.checkId}. Try again in a minute.</div>}
      {r && !r.pending && (
        <div style={{ marginTop: 10, fontSize: '0.8rem', color: 'var(--hp-text)', display: 'grid', gap: 4 }}>
          <div>Login and check: <strong style={{ color: 'var(--hp-green)' }}>connected</strong> · {r.seconds}s · check {r.checkId} · live setting: {r.configuredEnv}</div>
          {r.resultCode !== 'SUCCESS' ? (
            <div>Result <strong>{r.resultCode}</strong> — {r.resultText}</div>
          ) : (
            <>
              <div>VEVO: <strong>{ENT_LABEL[r.vevo.entitlement] || '—'}</strong> · subclass {r.vevo.subclass || '—'} {r.vevo.visaName && `(${r.vevo.visaName})`} · expires {r.vevo.noExpiry ? 'never' : fmtDate(r.vevo.expiryDate)} · conditions {r.vevo.conditions.join(', ') || 'none'}{r.vevo.applicant && ` · ${r.vevo.applicant}`}</div>
              <div>Our engine reads it as: <strong style={{ color: right.color }}>{right.label}</strong> — {r.workBasis}</div>
              <div>PDF: {r.pdf ? (r.pdf.error ? <span style={{ color: 'var(--hp-red)' }}>{r.pdf.error}</span> : `${r.pdf.isPdf ? 'downloaded' : 'downloaded but not a PDF'} (${Math.round(r.pdf.bytes / 1024)} KB)`) : 'none attached'}</div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Period presets. Australian financial years run 1 Jul – 30 Jun.
const PRESETS = [['fortnight', 'Last 2 weeks'], ['3m', 'Last 3 months'], ['fy', 'This financial year'], ['lastfy', 'Last financial year'], ['12m', 'Last 12 months'], ['custom', 'Custom']];
function presetRange(preset) {
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(new Date());
  const shift = (iso, days) => { const d = new Date(iso + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + days); return d.toISOString().slice(0, 10); };
  const [y, m] = today.split('-').map(Number);
  const fyStart = m >= 7 ? y : y - 1;
  switch (preset) {
    case 'fortnight': return { preset, from: shift(today, -13), to: today };
    case '3m': return { preset, from: shift(today, -91), to: today };
    case 'lastfy': return { preset, from: `${fyStart - 1}-07-01`, to: `${fyStart}-06-30` };
    case '12m': return { preset, from: shift(today, -364), to: today };
    default: return { preset: 'fy', from: `${fyStart}-07-01`, to: today };
  }
}

const ENT_LABEL = { UNLIMITED: 'Unlimited', LIMITED: 'Limited', NIL: 'No work (nil)' };
function vevoTip(v, count) {
  return [
    `VEVO check run ${fmtDate(v.checkedOn)}${v.checkedBy ? ` — recorded by ${v.checkedBy}` : ''}`,
    `Work entitlement: ${ENT_LABEL[v.entitlement] || '—'}`,
    v.subclass && `Subclass ${v.subclass}${v.visaName ? ` (${v.visaName})` : ''}`,
    `Expiry: ${v.noExpiry ? 'no expiry' : fmtDate(v.expiryDate)}`,
    v.conditions && v.conditions.length ? `Conditions: ${v.conditions.join(', ')}` : 'No conditions recorded',
    v.applicant && `Applicant: ${v.applicant}`,
    v.notes && `Notes: ${v.notes}`,
    count > 1 && `${count} checks on file`,
    'Click to open the result',
  ].filter(Boolean).join('\n');
}

// Text every visa holder who hasn't consented. Shows exactly who gets it and the words
// first; sends only on the second click. Anyone texted in the last day is skipped.
function ConsentSmsModal({ onClose, onSent, onError }) {
  const [prev, setPrev] = useState(null);
  const [sending, setSending] = useState(false);
  const [again, setAgain] = useState(false);
  useEffect(() => {
    call({ action: 'nudgeVevoConsent', dryRun: true }).then((d) => {
      if (d && d.success) setPrev(d); else { onError(d?.error || 'Could not load the list'); onClose(); }
    }).catch((e) => { onError(e.message); onClose(); });
  }, [onClose, onError]);
  const willSend = prev ? prev.recipients.filter((r) => r.hasMobile && (again || !r.recentlyTexted)) : [];
  const send = async () => {
    setSending(true);
    try {
      const d = await call({ action: 'nudgeVevoConsent', again });
      if (d && d.success) await onSent(d); else onError(d?.error || 'Could not send');
    } catch (e) { onError(e.message); } finally { setSending(false); }
  };
  return (
    <div role="dialog" aria-modal="true" onClick={sending ? undefined : onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--hp-bg, #fff)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', width: 'min(560px, 100%)', maxHeight: '92vh', overflowY: 'auto', padding: '20px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <MessageSquare size={18} color="var(--hp-gold)" />
          <h2 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--hp-text)' }}>Text consent request</h2>
          <button onClick={onClose} disabled={sending} style={{ ...linkBtn, marginLeft: 'auto' }} aria-label="Close"><X size={16} /></button>
        </div>
        {!prev ? <div style={{ color: 'var(--hp-dim)', fontSize: '0.84rem' }}><RefreshCw size={14} className="spin" /> Loading…</div> : (
          <>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>Message (drivers)</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--hp-text)', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '8px 10px', marginBottom: 6 }}>{prev.message.driver}</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginBottom: 12 }}>Night sorters get the same text, pointing to Documents in the sorter portal.</div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
              Sending to {willSend.length} {willSend.length === 1 ? 'person' : 'people'}
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--hp-text)', marginBottom: 10 }}>{willSend.map((r) => r.name).join(', ') || 'Nobody — everyone waiting has no mobile or was texted in the last day.'}</div>
            {prev.recipients.some((r) => !r.hasMobile) && (
              <div style={{ fontSize: '0.76rem', color: 'var(--hp-red)', marginBottom: 6 }}>No mobile on file: {prev.recipients.filter((r) => !r.hasMobile).map((r) => r.name).join(', ')}</div>
            )}
            {prev.recipients.some((r) => r.hasMobile && r.recentlyTexted) && (
              <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: '0.76rem', color: 'var(--hp-text)', marginBottom: 6, cursor: 'pointer' }}>
                <input type="checkbox" checked={again} onChange={(e) => setAgain(e.target.checked)} style={{ marginTop: 2 }} />
                <span>
                  Send again to the {prev.recipients.filter((r) => r.hasMobile && r.recentlyTexted).length} already texted in the last day
                  <span style={{ color: 'var(--hp-dim)' }}> — {prev.recipients.filter((r) => r.hasMobile && r.recentlyTexted).map((r) => r.name).join(', ')}</span>
                </span>
              </label>
            )}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={onClose} disabled={sending} style={{ ...ghostBtn, marginLeft: 'auto' }}>Cancel</button>
              <button onClick={send} disabled={sending || !willSend.length}
                style={{ ...ghostBtn, background: willSend.length ? 'var(--hp-gold)' : 'var(--hp-surface)', color: willSend.length ? '#1a1a2e' : 'var(--hp-dim)', borderColor: willSend.length ? 'var(--hp-gold)' : 'var(--hp-border)' }}>
                {sending ? 'Sending…' : `Send ${willSend.length} ${willSend.length === 1 ? 'text' : 'texts'}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Read a picked file as base64. Netlify caps a function body at 6 MB and base64
// inflates by a third, so anything past ~4.5 MB is refused here with a clear message.
const MAX_FILE = 4_500_000;
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^;]+;base64,/, ''));
    r.onerror = () => reject(new Error('Could not read the file'));
    r.readAsDataURL(file);
  });
}

function sydneyTodayISO() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Australia/Sydney' }).format(new Date());
}

// Record what VEVO said, with the result file as the evidence. Pre-filled from the
// last check (a re-check usually only changes the date and the file).
function VevoCheckModal({ holder, onClose, onSaved, onError }) {
  const last = holder.vevo || {};
  const [f, setF] = useState({
    checkedOn: sydneyTodayISO(),
    entitlement: last.entitlement || '',
    subclass: last.subclass || holder.subclass || '',
    visaName: last.visaName || '',
    expiryDate: last.expiryDate || holder.expiry || '',
    noExpiry: !!last.noExpiry,
    conditions: (last.conditions || holder.conditions || []).join(', '),
    applicant: last.applicant || (holder.secondary ? 'secondary' : ''),
    notes: '',
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const missing = !f.checkedOn ? 'the date the check was run'
    : !f.entitlement ? 'the work entitlement'
    : (!f.expiryDate && !f.noExpiry) ? 'the visa expiry'
    : !file ? 'the VEVO result file' : '';

  const save = async () => {
    if (missing) return;
    if (file.size > MAX_FILE) { onError('That file is over 4.5 MB — save the VEVO result as a PDF and attach that.'); return; }
    setSaving(true);
    try {
      const base64 = await fileToBase64(file);
      const d = await call({
        action: 'recordVevoCheck', driverLogin: holder.login, vevo: f,
        fileBase64: base64, contentType: file.type || 'application/pdf', filename: file.name,
      });
      if (d && d.success) await onSaved();
      else onError(d?.error || 'Could not record the check');
    } catch (e) { onError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div role="dialog" aria-modal="true" onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--hp-bg, #fff)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', width: 'min(560px, 100%)', maxHeight: '92vh', overflowY: 'auto', padding: '20px 22px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <ShieldCheck size={18} color="var(--hp-gold)" />
          <h2 style={{ margin: 0, fontSize: '1.1rem', color: 'var(--hp-text)' }}>Record VEVO check — {holder.name}</h2>
          <button onClick={onClose} style={{ ...linkBtn, marginLeft: 'auto' }} aria-label="Close"><X size={16} /></button>
        </div>
        <p style={{ margin: '0 0 16px', fontSize: '0.78rem', color: 'var(--hp-dim)' }}>
          Copy what the VEVO result says and attach the result itself. Home Affairs' answer outranks the captured visa details,
          and the expiry below becomes the visa's expiry for renewal reminders.
        </p>

        <Row label="Date the check was run">
          <input type="date" value={f.checkedOn} max={sydneyTodayISO()} onChange={set('checkedOn')} style={inp} />
        </Row>
        <Row label="Work entitlement">
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {Object.entries(ENT_LABEL).map(([k, l]) => (
              <label key={k} style={radioLbl}>
                <input type="radio" name="ent" value={k} checked={f.entitlement === k} onChange={set('entitlement')} /> {l}
              </label>
            ))}
          </div>
        </Row>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <Row label="Visa subclass" grow><input value={f.subclass} onChange={set('subclass')} placeholder="e.g. 500" style={inp} inputMode="numeric" /></Row>
          <Row label="Visa name (optional)" grow><input value={f.visaName} onChange={set('visaName')} placeholder="e.g. Student" style={inp} /></Row>
        </div>
        <Row label="Visa expiry">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <input type="date" value={f.expiryDate} disabled={f.noExpiry} onChange={set('expiryDate')} style={inp} />
            <label style={radioLbl}><input type="checkbox" checked={f.noExpiry} onChange={set('noExpiry')} /> No expiry (permanent)</label>
          </div>
        </Row>
        <Row label="Visa conditions" hint="The 8xxx codes VEVO lists — 8105 = 48h student cap, 8104 = dependant, 8101 = no work.">
          <input value={f.conditions} onChange={set('conditions')} placeholder="e.g. 8105, 8202, 8501" style={inp} />
        </Row>
        <Row label="Applicant">
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {[['primary', 'Primary'], ['secondary', 'Secondary (partner / dependant)'], ['', 'Not shown']].map(([k, l]) => (
              <label key={k || 'none'} style={radioLbl}>
                <input type="radio" name="app" value={k} checked={f.applicant === k} onChange={set('applicant')} /> {l}
              </label>
            ))}
          </div>
        </Row>
        <Row label="Notes (optional)">
          <input value={f.notes} onChange={set('notes')} placeholder="e.g. run via vSure web app" style={inp} />
        </Row>
        <Row label="VEVO result (PDF or screenshot)">
          <label style={{ ...ghostBtn, cursor: 'pointer' }}>
            <FileText size={14} /> {file ? file.name : 'Choose file…'}
            <input type="file" accept="application/pdf,image/*" hidden onChange={(e) => setFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)} />
          </label>
        </Row>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 18 }}>
          {missing && <span style={{ fontSize: '0.74rem', color: 'var(--hp-dim)' }}>Still needed: {missing}</span>}
          <button onClick={onClose} style={{ ...ghostBtn, marginLeft: 'auto' }}>Cancel</button>
          <button onClick={save} disabled={!!missing || saving}
            style={{ ...ghostBtn, background: missing ? 'var(--hp-surface)' : 'var(--hp-green)', color: missing ? 'var(--hp-dim)' : '#fff', borderColor: missing ? 'var(--hp-border)' : 'var(--hp-green)', cursor: missing ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Saving…' : 'Save check'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, hint, grow, children }) {
  return (
    <div style={{ marginBottom: 12, flex: grow ? '1 1 200px' : undefined }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      {children}
      {hint && <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

const inp = { padding: '7px 9px', fontSize: '0.84rem', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)', color: 'var(--hp-text)', width: '100%', boxSizing: 'border-box' };
const radioLbl = { display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.82rem', color: 'var(--hp-text)', cursor: 'pointer' };
const linkBtn = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: 0, fontSize: '0.72rem', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--hp-dim)' };

function Tile({ label, value, color, icon }) {
  return (
    <div style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '14px 18px', background: 'var(--hp-surface)', minWidth: 130 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--hp-dim)', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{icon} {label}</div>
      <div style={{ fontSize: '1.7rem', fontWeight: 800, color, marginTop: 4 }}>{value}</div>
    </div>
  );
}

const ghostBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', padding: '6px 12px', fontSize: '0.8rem', fontWeight: 700, border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: 'var(--hp-surface)', color: 'var(--hp-text)', cursor: 'pointer' };
